import React from 'react';

// ── Staff surfaces (Design Wave 3, 2026-09-28) ─────────────────────────────
// The card, tile, label, button and skeleton recipes Waves 1–2 repeated on
// Home, Decide, desk Today and the Action Center — one copy here. Everything
// rides the tailwind tokens (tailwind.config.js): radius control/card/sheet
// (12/14/22), elevation e1/e2, type scale title/number/card-title/ui/context/
// meta/caps, and the color tokens (which flip for dark mode on their own).
//
// Status colors are StatusChip's five meanings (ui.jsx): neutral grey, teal
// "waiting on Mark", soft-coral warn, coral urgent, teal success.

export const SURFACE = {
  // A card one step above the page: tiles, list cards, rail cards.
  card: 'rounded-card border border-outline-variant/70 bg-surface-container-lowest shadow-e1',
  // Small caps section / tile label ("At a glance", "Approvals").
  label: 'font-label text-caps font-medium uppercase text-on-surface-variant',
  // Card title (Inter 15.5 semibold) and its one line of context.
  cardTitle: 'font-body text-card-title font-semibold text-on-surface m-0',
  context: 'text-context text-on-surface-variant',
  meta: 'text-meta text-on-surface-variant',
  // Page title — serif, only for page titles and big numbers.
  pageTitle: 'font-headline font-medium text-title text-on-surface m-0',
  bigNumber: 'font-headline text-number tabular-nums',
  // The one filled button on a card (teal — deciding is normal work) and the
  // quiet outline companion. 44 px tall, radius 12.
  button:
    'press inline-flex min-h-11 items-center gap-1.5 rounded-control bg-primary px-4 text-sm font-label text-on-primary no-underline hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary',
  buttonQuiet:
    'press inline-flex min-h-11 items-center rounded-control border border-primary/40 px-4 text-sm font-label text-primary no-underline hover:bg-primary/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary',
  // Inner row / quoted script inside a card.
  inset: 'rounded-control border border-outline-variant/70 bg-surface-container-low',
};

export function Card({ as: Tag = 'div', className = '', children, ...rest }) {
  return (
    <Tag className={`${SURFACE.card} ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

export function SectionLabel({ as: Tag = 'h2', className = '', children, ...rest }) {
  return (
    <Tag className={`${SURFACE.label} m-0 ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

// ── Tile ──────────────────────────────────────────────────────────────────
// Quiet by default, loud only when late (Iris, owner-approved): every tile is
// the same card. `warn` adds a 4 px soft-coral edge; only `late` turns the
// edge, border and number coral. A tile that just turned late pulses ONCE
// (never blinks forever; off under reduced motion).
export const TILE_TONES = {
  late: { box: 'border-secondary/45', edge: 'bg-secondary', num: 'text-secondary', chip: 'urgent' },
  warn: { box: 'border-outline-variant/70', edge: 'bg-warn', num: 'text-on-surface', chip: 'warn' },
  neutral: { box: 'border-outline-variant/70', edge: null, num: 'text-on-surface', chip: 'neutral' },
};

function usePrevious(value) {
  const ref = React.useRef(value);
  React.useEffect(() => { ref.current = value; }, [value]);
  return ref.current;
}

export function Tile({ tone = 'neutral', wide = false, className = '', children }) {
  const t = TILE_TONES[tone] || TILE_TONES.neutral;
  const prev = usePrevious(tone);
  const justTurnedLate = tone === 'late' && prev !== 'late';
  return (
    <div
      className={`press relative overflow-hidden flex flex-col rounded-card border bg-surface-container-lowest shadow-e1 hover:bg-surface-container-low ${t.box} ${
        wide ? 'col-span-2 lg:col-span-1' : ''
      } ${justTurnedLate && prev ? 'motion-safe:animate-pulse-once' : ''} ${className}`}
    >
      {t.edge && <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${t.edge}`} />}
      {children}
    </div>
  );
}

// ── StatTile ──────────────────────────────────────────────────────────────
// A page's summary number that is NOT a link (Invoices "Total overdue", At
// Risk "High risk"): the Tile shell and tones without the press/hover a
// tappable tile carries. `size="sm"` keeps three money tiles on one 375 px
// phone row. Design review 2026-10-07.
export function StatTile({ label, value, suffix = null, tone = 'neutral', size = 'md', className = '' }) {
  const t = TILE_TONES[tone] || TILE_TONES.neutral;
  const num = size === 'sm' ? 'font-headline text-xl sm:text-2xl tabular-nums' : SURFACE.bigNumber;
  return (
    <div className={`relative overflow-hidden rounded-card border bg-surface-container-lowest shadow-e1 pl-4 pr-3 pt-3 pb-3 min-w-0 ${t.box} ${className}`}>
      {t.edge && <span aria-hidden="true" className={`absolute inset-y-0 left-0 w-1 ${t.edge}`} />}
      <div className={SURFACE.label}>{label}</div>
      <div className={`mt-1.5 ${num} ${t.num}`}>
        <CountFade value={value} />
        {suffix && <span className="ml-1 font-body text-meta text-on-surface-variant">{suffix}</span>}
      </div>
    </div>
  );
}

// ── CountFade ─────────────────────────────────────────────────────────────
// A number that changes on refresh cross-fades over 150 ms. It never counts up.
export function CountFade({ value, className = '' }) {
  return (
    <span key={String(value)} className={`inline-block motion-safe:animate-count-in ${className}`}>
      {value}
    </span>
  );
}

// ── Loading skeletons ─────────────────────────────────────────────────────
// Per-card placeholders (H1): each card shows its own skeleton and fills in as
// soon as ITS data arrives. They stay invisible for the first 300 ms so fast
// loads never flash. Decorative only; the owning card carries the
// role="status" text.
export function SkeletonLine({ className = '' }) {
  return <span className={`block h-3 rounded bg-surface-container-high motion-safe:animate-pulse ${className}`} />;
}

export function TileSkeleton() {
  return (
    <div className={`${SURFACE.card} animate-appear-late pl-4 pr-3 pt-3.5 pb-4 min-h-[6.5rem]`}>
      <SkeletonLine className="w-16" />
      <SkeletonLine className="mt-3 h-7 w-8" />
      <SkeletonLine className="mt-3 w-24" />
    </div>
  );
}

export function RowsSkeleton({ rows = 2, label }) {
  return (
    <div className="px-4 py-3" role="status" aria-live="polite">
      <span className="sr-only">{label}</span>
      <div aria-hidden="true" className="space-y-3 animate-appear-late">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="flex items-center justify-between gap-3">
            <SkeletonLine className="w-32" />
            <SkeletonLine className="w-14" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Check badge ───────────────────────────────────────────────────────────
// Teal circle + check: "done / nothing needs you" (teal means done).
export function CheckBadge({ size = 'md' }) {
  const box = size === 'lg' ? 'w-11 h-11' : 'w-9 h-9';
  const icon = size === 'lg' ? 'w-6 h-6' : 'w-5 h-5';
  return (
    <span aria-hidden="true" className={`inline-flex ${box} shrink-0 items-center justify-center rounded-full bg-primary-fixed text-on-primary-fixed-variant`}>
      <svg viewBox="0 0 20 20" className={icon} fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M5 10.5l3.2 3.2L15 7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}
